import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// ─── Types ───────────────────────────────────────────────────────────────────

type LangKey = 'ja' | 'en' | 'zh' | 'zh-TW' | 'ko' | 'fr' | 'de' | 'es' | 'ru' | 'ar' | 'hi' | 'th' | 'vi' | 'id' | 'tl' | 'ne' | 'pt' | 'it';

interface SeoEntry {
  title: string;
  description: string;
  keywords: string;
  ogLocale: string;
  htmlLang: string;
}

// ─── SEO config per language ─────────────────────────────────────────────────

const SEO_CONFIG: Record<LangKey, SeoEntry> = {
  ja: {
    title: '沖縄姓名判断・てんてんおじさん鑑定所｜命名・改名・法人名鑑定',
    description: '口コミだけで全国から依頼が集まる、沖縄・伊計島の姓名判断師「てんてんおじさん」への代行鑑定サービス。改名不要、名前にそっと「てんてん」を添えるだけで運の流れが整います。毎月先着100名・最短1ヶ月以内にお届け。全国オンライン完結。',
    keywords: '姓名判断,沖縄,命名鑑定,てんてんおじさん,改名,法人名鑑定',
    ogLocale: 'ja_JP',
    htmlLang: 'ja',
  },
  en: {
    title: 'Okinawa Name Reading · Tenten-Ojisan | Baby Naming, Renaming & Business Name',
    description: 'A proxy reading service for Tenten-Ojisan, the word-of-mouth name master of Ikei Island, Okinawa. No name change needed — just a gentle ten-ten added to your name aligns the flow of fortune. First 100 applicants monthly, delivered within one month. 100% online.',
    keywords: 'name reading,Okinawa,baby naming,Tenten-Ojisan,renaming,business name divination',
    ogLocale: 'en_US',
    htmlLang: 'en',
  },
  zh: {
    title: '冲绳姓名鉴定・点点大叔鉴定所｜命名・改名・商号鉴定',
    description: '口耳相传、全国慕名而来——冲绳伊计岛姓名鉴定师「点点大叔」代行鉴定服务。无需改名，只需在名字上轻轻添上「点点」，即可调整运势流向。每月限额100名，最快1个月内送达。全程线上完结。',
    keywords: '姓名鉴定,冲绳,命名,点点大叔,改名,商号鉴定',
    ogLocale: 'zh_CN',
    htmlLang: 'zh-Hans',
  },
  'zh-TW': {
    title: '沖繩姓名鑑定・點點大叔鑑定所｜命名・改名・商號鑑定',
    description: '口耳相傳、全國慕名而來——沖繩伊計島姓名鑑定師「點點大叔」代行鑑定服務。無需改名，只需在名字上輕輕添上「點點」，即可調整運勢流向。每月限額100名，最快1個月內送達。全程線上完結。',
    keywords: '姓名鑑定,沖繩,命名,點點大叔,改名,商號鑑定',
    ogLocale: 'zh_TW',
    htmlLang: 'zh-Hant',
  },
  ko: {
    title: '오키나와 이름 감정 · 텐텐 아저씨 감정소 | 작명·개명·상호 감정',
    description: '입소문만으로 전국에서 의뢰가 쇄도하는 오키나와 이케이섬의 이름 감정사 「텐텐 아저씨」 대행 감정 서비스. 개명 불필요 — 이름에 살며시 「텐텐」을 더하는 것만으로 운의 흐름이 정돈됩니다. 매월 선착순 100명, 최단 1개월 이내 전달. 전국 온라인 완결.',
    keywords: '이름감정,오키나와,작명,텐텐아저씨,개명,상호감정',
    ogLocale: 'ko_KR',
    htmlLang: 'ko',
  },
  fr: {
    title: 'Lecture de Noms Okinawa · Tenten-Ojisan | Prénoms, Renommage & Nom Commercial',
    description: 'Service de lecture de noms par procuration auprès de Tenten-Ojisan, le maître de l\'île d\'Ikei, Okinawa, connu uniquement par le bouche-à-oreille. Aucun changement de nom requis — un doux ten-ten suffit à harmoniser le flux de votre destin. 100 places par mois, livraison sous un mois. Entièrement en ligne.',
    keywords: 'lecture de noms,Okinawa,prénom bébé,Tenten-Ojisan,renommage,nom commercial',
    ogLocale: 'fr_FR',
    htmlLang: 'fr',
  },
  de: {
    title: 'Okinawa Namenslesung · Tenten-Ojisan | Babyname, Umbenennung & Firmenname',
    description: 'Proxy-Leseservice für Tenten-Ojisan, den nur durch Mundpropaganda bekannten Namensmeister von Ikei Island, Okinawa. Keine Namensänderung nötig — ein sanftes Ten-Ten harmonisiert den Fluss Ihres Schicksals. Monatlich 100 Plätze, Lieferung innerhalb eines Monats. Vollständig online.',
    keywords: 'Namenslesung,Okinawa,Babyname,Tenten-Ojisan,Umbenennung,Firmenname',
    ogLocale: 'de_DE',
    htmlLang: 'de',
  },
  es: {
    title: 'Lectura de Nombres Okinawa · Tenten-Ojisan | Nombres de Bebé, Cambio de Nombre',
    description: 'Servicio de lectura de nombres por intermediario con Tenten-Ojisan, el maestro de la isla Ikei, Okinawa, conocido solo por el boca a boca. Sin cambio de nombre — un suave ten-ten armoniza el flujo de tu destino. 100 plazas al mes, entrega en un mes. Todo online.',
    keywords: 'lectura de nombres,Okinawa,nombre bebé,Tenten-Ojisan,cambio de nombre,nombre comercial',
    ogLocale: 'es_ES',
    htmlLang: 'es',
  },
  ru: {
    title: 'Чтение имён Окинава · Тентен-Одзисан | Имена для детей, переименование',
    description: 'Посреднический сервис чтения имён у Тентен-Одзисана — мастера острова Икэй, Окинава, известного только по сарафанному радио. Менять имя не нужно — достаточно добавить мягкий тен-тен, чтобы поток судьбы выровнялся. 100 мест в месяц, доставка в течение месяца. Полностью онлайн.',
    keywords: 'чтение имён,Окинава,имя ребёнка,Тентен-Одзисан,переименование',
    ogLocale: 'ru_RU',
    htmlLang: 'ru',
  },
  ar: {
    title: 'قراءة الأسماء أوكيناوا · العم تنتن | تسمية الأطفال وتغيير الأسماء',
    description: 'خدمة قراءة الأسماء بالوكالة مع العم تنتن، خبير جزيرة إيكي، أوكيناوا، المعروف بالتوصيات الشفهية فقط. لا حاجة لتغيير الاسم — يكفي إضافة علامة تن-تن بلطف لتنسيق تدفق الحظ. 100 مقعد شهريًا، التسليم خلال شهر. كل شيء عبر الإنترنت.',
    keywords: 'قراءة الأسماء,أوكيناوا,تسمية الأطفال,العم تنتن,تغيير الاسم',
    ogLocale: 'ar_SA',
    htmlLang: 'ar',
  },
  hi: {
    title: 'ओकिनावा नाम पठन · तेंतेन अंकल | नामकरण, नाम परिवर्तन',
    description: 'केवल मुँह-ज़बानी प्रसिद्ध, ओकिनावा के इकेई द्वीप के नाम विशेषज्ञ "तेंतेन अंकल" की प्रॉक्सी नाम पठन सेवा। नाम बदलने की जरूरत नहीं — बस नाम में हल्का तेन-तेन जोड़ने से भाग्य का प्रवाह संतुलित होता है। हर महीने 100 स्थान, एक महीने में डिलीवरी। पूरी तरह ऑनलाइन।',
    keywords: 'नाम पठन,ओकिनावा,नामकरण,तेंतेन अंकल,नाम परिवर्तन',
    ogLocale: 'hi_IN',
    htmlLang: 'hi',
  },
  th: {
    title: 'อ่านชื่อโอกินาวา · ลุงเทนเทน | ตั้งชื่อทารก เปลี่ยนชื่อ ชื่อธุรกิจ',
    description: 'บริการอ่านชื่อแบบตัวแทนกับลุงเทนเทน ผู้เชี่ยวชาญที่มีชื่อเสียงจากปากต่อปากแห่งเกาะอิเคอิ โอกินาวา ไม่ต้องเปลี่ยนชื่อ เพียงเพิ่ม "เทนเทน" เบาๆ ลงในชื่อก็ช่วยปรับกระแสโชคชะตา รับ 100 คนต่อเดือน ส่งมอบภายใน 1 เดือน ทำได้ทุกอย่างออนไลน์',
    keywords: 'อ่านชื่อ,โอกินาวา,ตั้งชื่อทารก,ลุงเทนเทน,เปลี่ยนชื่อ',
    ogLocale: 'th_TH',
    htmlLang: 'th',
  },
  vi: {
    title: 'Xem Tên Okinawa · Chú Tenten | Đặt Tên Em Bé, Đổi Tên, Tên Doanh Nghiệp',
    description: 'Dịch vụ xem tên qua trung gian với Chú Tenten — chuyên gia nổi tiếng chỉ qua truyền miệng tại đảo Ikei, Okinawa. Không cần đổi tên, chỉ cần thêm nhẹ nhàng dấu "ten-ten" vào tên là dòng chảy vận may được cân bằng. 100 suất mỗi tháng, giao kết quả trong vòng 1 tháng. Hoàn toàn trực tuyến.',
    keywords: 'xem tên,Okinawa,đặt tên em bé,Chú Tenten,đổi tên',
    ogLocale: 'vi_VN',
    htmlLang: 'vi',
  },
  id: {
    title: 'Konsultasi Nama Okinawa · Paman Tenten | Nama Bayi, Ganti Nama, Nama Bisnis',
    description: 'Layanan pembacaan nama melalui perantara bersama Paman Tenten, ahli nama dari Pulau Ikei, Okinawa yang terkenal dari mulut ke mulut. Tidak perlu ganti nama — cukup tambahkan ten-ten lembut pada nama Anda untuk menyelaraskan aliran keberuntungan. 100 tempat per bulan, dikirim dalam sebulan. Sepenuhnya online.',
    keywords: 'konsultasi nama,Okinawa,nama bayi,Paman Tenten,ganti nama',
    ogLocale: 'id_ID',
    htmlLang: 'id',
  },
  tl: {
    title: 'Konsultasyon sa Pangalan Okinawa · Tito Tenten | Pangalan ng Sanggol, Pagpapalit ng Pangalan',
    description: 'Serbisyo ng proxy na pagbabasa ng pangalan kasama si Tito Tenten, ang kilalang eksperto sa pangalan mula sa Ikei Island, Okinawa na nakilala lamang sa salita ng bibig. Hindi kailangang palitan ang pangalan — ang malambot na ten-ten ay nagtatakda ng daloy ng kapalaran. 100 puwesto bawat buwan, maihahatid sa loob ng isang buwan. Lahat online.',
    keywords: 'konsultasyon sa pangalan,Okinawa,pangalan ng sanggol,Tito Tenten,pagpapalit ng pangalan',
    ogLocale: 'tl_PH',
    htmlLang: 'tl',
  },
  ne: {
    title: 'ओकिनावा नाम परामर्श · टेन्टेन अंकल | नामकरण, नाम परिवर्तन',
    description: 'केवल मुखबाट प्रसिद्ध, ओकिनावाको इकेई टापुका नाम विशेषज्ञ "टेन्टेन अंकल"को प्रोक्सी नाम पठन सेवा। नाम परिवर्तन आवश्यक छैन — नाममा हल्का तेन-तेन थप्नु मात्रले भाग्यको प्रवाह मिलाउँछ। प्रत्येक महिना १०० स्थान, एक महिनाभित्र डेलिभरी। पूर्णतः अनलाइन।',
    keywords: 'नाम परामर्श,ओकिनावा,नामकरण,टेन्टेन अंकल,नाम परिवर्तन',
    ogLocale: 'ne_NP',
    htmlLang: 'ne',
  },
  pt: {
    title: 'Leitura de Nome Okinawa · Tio Tenten | Nome de Bebê, Renomeação, Nome Comercial',
    description: 'Serviço de leitura de nomes por procuração com Tio Tenten, o mestre da Ilha Ikei, Okinawa, conhecido apenas pelo boca a boca. Sem necessidade de mudar o nome — basta adicionar suavemente um ten-ten para alinhar o fluxo da sorte. 100 vagas por mês, entrega em até um mês. Totalmente online.',
    keywords: 'leitura de nome,Okinawa,nome de bebê,Tio Tenten,renomeação,nome comercial',
    ogLocale: 'pt_BR',
    htmlLang: 'pt',
  },
  it: {
    title: 'Lettura del Nome Okinawa · Tenten-Ojisan | Nome Neonato, Rinomina, Nome Aziendale',
    description: 'Servizio di lettura del nome per procura con Tenten-Ojisan, il maestro dell\'isola di Ikei, Okinawa, noto solo per il passaparola. Nessun cambio di nome necessario — basta aggiungere delicatamente un ten-ten per armonizzare il flusso della fortuna. 100 posti al mese, consegna entro un mese. Tutto online.',
    keywords: 'lettura del nome,Okinawa,nome neonato,Tenten-Ojisan,rinomina,nome aziendale',
    ogLocale: 'it_IT',
    htmlLang: 'it',
  },
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LOGO_URL = 'https://public.readdy.ai/ai/img_res/edited_3693c096081961b489aaeeec35ce6ce4_ea61dad8.jpg';
const SITE_URL = 'https://tenten.okinawa/';

// ─── Schema builders ─────────────────────────────────────────────────────────

const buildLocalBusiness = (lang: LangKey) => {
  const isJa = lang === 'ja';
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: isJa ? 'てんてんおじさん鑑定所' : 'Tenten-Ojisan Divination',
    alternateName: isJa ? '沖縄姓名判断・てんてんおじさん鑑定所' : 'Okinawa Name Reading · Tenten-Ojisan',
    description: SEO_CONFIG[lang].description,
    url: SITE_URL,
    logo: LOGO_URL,
    image: LOGO_URL,
    email: 'tenten.sensei.okinawa@gmail.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: isJa ? 'うるま市伊計島' : 'Ikei Island, Uruma',
      addressRegion: isJa ? '沖縄県' : 'Okinawa',
      addressCountry: 'JP',
    },
    areaServed: { '@type': 'Country', name: 'Japan' },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    priceRange: '¥11,000–¥44,000',
    currenciesAccepted: 'JPY',
    paymentAccepted: isJa ? '銀行振込' : 'Bank Transfer',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: isJa ? '姓名判断鑑定メニュー' : 'Name Reading Services',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: isJa ? '個人のお名前鑑定（16歳以上）' : 'Personal Name Reading (Age 16+)' }, price: '22000', priceCurrency: 'JPY' },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: isJa ? '個人のお名前鑑定（4〜15歳）' : 'Personal Name Reading (Age 4–15)' }, price: '33000', priceCurrency: 'JPY' },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: isJa ? '個人のお名前鑑定（0〜3歳）' : 'Personal Name Reading (Age 0–3)' }, price: '44000', priceCurrency: 'JPY' },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: isJa ? '屋号・ビジネスネーム鑑定' : 'Business / Shop Name Reading' }, price: '44000', priceCurrency: 'JPY' },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: isJa ? 'ペットのお名前鑑定' : 'Pet Name Reading' }, price: '11000', priceCurrency: 'JPY' },
      ],
    },
  };
};

const buildPerson = (lang: LangKey) => {
  const isJa = lang === 'ja';
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: isJa ? 'てんてんおじさん' : 'Tenten-Ojisan',
    jobTitle: isJa ? '姓名判断鑑定士' : 'Name Reading Master',
    description: isJa
      ? '沖縄・伊計島を拠点に活動する姓名判断鑑定士。元銀行員から転身し、50年以上にわたり姓名判断を研究・実践。全国から依頼が集まる人気鑑定士。'
      : 'A name-reading master based on Ikei Island, Okinawa. A former banker who devoted himself to name reading for over 50 years. Requests come from all over Japan.',
    worksFor: {
      '@type': 'Organization',
      name: isJa ? 'てんてんおじさん鑑定所' : 'Tenten-Ojisan Divination',
      url: SITE_URL,
    },
    knowsAbout: isJa
      ? ['姓名判断', '命名', '改名', '開運', '濁点', '姓名鑑定']
      : ['name reading', 'baby naming', 'renaming', 'fortune', 'ten-ten', 'Japanese name divination'],
    image: LOGO_URL,
    url: SITE_URL,
  };
};

const buildBreadcrumbList = (lang: LangKey) => {
  const isJa = lang === 'ja';
  const sections = isJa
    ? [
        { name: 'ホーム', id: '' },
        { name: 'てんてんおじさんとは', id: 'about' },
        { name: '鑑定メニュー・料金', id: 'services' },
        { name: 'ご利用の流れ', id: 'flow' },
        { name: 'お客様の声', id: 'testimonials' },
        { name: 'よくある質問', id: 'faq' },
        { name: 'お問い合わせ', id: 'contact' },
      ]
    : [
        { name: 'Home', id: '' },
        { name: 'About', id: 'about' },
        { name: 'Services', id: 'services' },
        { name: 'How It Works', id: 'flow' },
        { name: 'Testimonials', id: 'testimonials' },
        { name: 'FAQ', id: 'faq' },
        { name: 'Contact', id: 'contact' },
      ];

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: sections.map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: s.name,
      item: s.id ? `${SITE_URL}#${s.id}` : SITE_URL,
    })),
  };
};

const buildFaqPage = (lang: LangKey) => {
  const isJa = lang === 'ja';
  const faqs = isJa
    ? [
        { q: '名前を変えないと意味がありませんか？', a: 'いいえ、改名はしませんし、必要ありません。今のお名前に、そっと「てんてん（濁点）」を添えて整えます。' },
        { q: '「てんてん」をつけるだけで、変化があるんですか？', a: '急激な変化というより、心や運の流れがやさしく整うイメージです。' },
        { q: 'オンラインで完結しますか？', a: 'はい。お申し込みから受け取りまで、すべてオンラインで完結します。' },
        { q: '実際に沖縄へ行く必要はありますか？', a: 'ありません。私が毎月、直接沖縄の島へ伺い、先生にお伝えします。' },
        { q: 'いつ結果が届きますか？', a: '最短で1ヶ月以内のご案内をしています。（毎月先着100名）' },
        { q: '料金以外に追加費用はありますか？', a: 'いいえ、追加費用はありません。鑑定料・代行費・郵送費も含まれています。' },
        { q: '支払い方法は？', a: '銀行振込（前払い）です。申し込み後に振込先をご案内します。' },
        { q: '子どもの名前も見てもらえますか？', a: 'はい、可能です。年齢に応じたメニューがあります。' },
        { q: '屋号やビジネスネームも鑑定できますか？', a: 'はい、できます。開業準備中の方、事業名を整えたい方にも人気です。' },
        { q: 'ペットの名前も対象ですか？', a: 'はい。家族の一員として、ペットのお名前も整えられます。' },
        { q: '本名を知られるのが不安です…', a: '個人情報は厳重に管理し、鑑定以外には使用しませんのでご安心ください。' },
        { q: '振込後にキャンセルできますか？', a: '鑑定開始前であればキャンセル可能です。開始後は対応できませんのでご了承ください。' },
      ]
    : [
        { q: 'Do I need to change my name?', a: 'No, you do not need to change your name. We simply add a gentle "ten-ten (voiced mark)" to your existing name.' },
        { q: 'Can just adding ten-ten really make a difference?', a: 'Rather than a dramatic change, think of it as gently harmonizing the flow of your heart and fortune.' },
        { q: 'Can everything be done online?', a: 'Yes. Everything from application to delivery is completed entirely online.' },
        { q: 'Do I need to travel to Okinawa?', a: 'No. I visit the island in Okinawa on your behalf every month and personally deliver your name to the master.' },
        { q: 'When will I receive my results?', a: 'We aim to deliver within one month at the earliest. (First 100 applicants per month)' },
        { q: 'Are there any additional fees beyond the listed price?', a: 'No, there are no additional fees. The reading fee, proxy service fee, and postage are all included.' },
        { q: 'What payment methods are accepted?', a: 'Bank transfer (prepayment). We will provide bank details after your application.' },
        { q: "Can you read my child's name?", a: 'Yes. We have services tailored to different age groups.' },
        { q: 'Can you read business or shop names?', a: 'Yes. This service is also popular with those preparing to open a business or wanting to refine their business name.' },
        { q: 'Are pet names also eligible?', a: "Yes. As a member of the family, your pet's name can also be harmonized." },
        { q: "I'm worried about sharing my real name…", a: 'Personal information is strictly managed and will not be used for any purpose other than the reading. Please feel at ease.' },
        { q: 'Can I cancel after transferring payment?', a: 'Cancellation is possible before the reading begins. Once started, we are unable to accommodate cancellations.' },
      ];

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
};

const buildWebSite = (lang: LangKey) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: lang === 'ja' ? 'てんてんおじさん鑑定所' : 'Tenten-Ojisan Divination',
  url: SITE_URL,
  inLanguage: SEO_CONFIG[lang].htmlLang,
  publisher: {
    '@type': 'Organization',
    name: lang === 'ja' ? 'てんてんおじさん鑑定所' : 'Tenten-Ojisan Divination',
    logo: { '@type': 'ImageObject', url: LOGO_URL },
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const setMeta = (name: string, content: string, isProperty = false) => {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const updateJsonLd = (id: string, data: object) => {
  let el = document.querySelector<HTMLScriptElement>(`script[data-schema-id="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-schema-id', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data, null, 2);
};

const getLangKey = (lang: string): LangKey => {
  if (lang in SEO_CONFIG) return lang as LangKey;
  // handle variants like 'zh-CN' → 'zh', 'zh-HK' → 'zh-TW'
  if (lang.startsWith('zh-TW') || lang.startsWith('zh-Hant') || lang === 'zh-HK') return 'zh-TW';
  if (lang.startsWith('zh')) return 'zh';
  return 'ja';
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useSeoMeta = () => {
  const { i18n } = useTranslation();
  const langKey = getLangKey(i18n.language);
  const config = SEO_CONFIG[langKey];

  useEffect(() => {
    // html lang attribute
    document.documentElement.lang = config.htmlLang;

    // Primary meta
    document.title = config.title;
    setMeta('description', config.description);
    setMeta('keywords', config.keywords);

    // Open Graph
    setMeta('og:title', config.title, true);
    setMeta('og:description', config.description, true);
    setMeta('og:locale', config.ogLocale, true);

    // Twitter / X
    setMeta('twitter:title', config.title);
    setMeta('twitter:description', config.description);

    // Disable original static JSON-LD scripts (avoid duplicates)
    document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]:not([data-schema-id])').forEach((s) => {
      s.setAttribute('data-disabled', 'true');
      s.removeAttribute('type');
    });

    // Dynamic Schema.org JSON-LD
    updateJsonLd('local-business', buildLocalBusiness(langKey));
    updateJsonLd('person', buildPerson(langKey));
    updateJsonLd('breadcrumb', buildBreadcrumbList(langKey));
    updateJsonLd('faq-page', buildFaqPage(langKey));
    updateJsonLd('web-site', buildWebSite(langKey));
  }, [langKey, config]);
};
